import { useState } from "react";
import { useTranslation } from "react-i18next";

interface BlacklistPanelProps {
  blacklist: string[];
  loading: boolean;
  onAdd: (number: string) => Promise<boolean>;
  onRemove: (number: string) => Promise<boolean>;
}

export default function BlacklistPanel({ blacklist, loading, onAdd, onRemove }: BlacklistPanelProps) {
  const { t } = useTranslation("dashboard");
  const [newNumber, setNewNumber] = useState("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [actionNum, setActionNum] = useState<string | null>(null);

  const showFeedback = (type: "success" | "error", msg: string) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 3000);
  };

  const handleAdd = async () => {
    const num = newNumber.trim();
    if (!num) return;
    if (!/^\d{10,15}$/.test(num)) {
      showFeedback("error", t("blacklist.invalidPhone"));
      return;
    }
    setActionNum(num);
    const ok = await onAdd(num);
    setActionNum(null);
    if (ok) {
      setNewNumber("");
      showFeedback("success", t("blacklist.addedSuccess"));
    } else {
      showFeedback("error", t("blacklist.addedFailed"));
    }
  };

  const handleRemove = async (num: string) => {
    setActionNum(num);
    const ok = await onRemove(num);
    setActionNum(null);
    showFeedback(ok ? "success" : "error", ok ? t("blacklist.removedSuccess") : t("blacklist.removedFailed"));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleAdd();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-foreground-950">{t("blacklist.title")}</h3>
        {feedback && (
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${feedback.type === "success" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
            {feedback.msg}
          </span>
        )}
      </div>

      <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
        <p className="text-foreground-950 font-semibold text-base mb-3">{t("blacklist.addNumber")}</p>
        <div className="flex gap-3">
          <input
            type="text"
            value={newNumber}
            onChange={(e) => setNewNumber(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("blacklist.phonePlaceholder")}
            className="flex-1 px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
          <button
            onClick={handleAdd}
            disabled={loading || !newNumber.trim()}
            className="px-5 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            {t("blacklist.block")}
          </button>
        </div>
      </div>

      {blacklist.length > 0 ? (
        <div className="bg-background-50 rounded-2xl border border-background-200/50 divide-y divide-background-200/30">
          {blacklist.map((num) => (
            <div key={num} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <i className="ri-forbid-2-line text-red-500 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-800 font-medium">{num}</span>
              </div>
              <button
                onClick={() => handleRemove(num)}
                disabled={loading || actionNum === num}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {actionNum === num ? (
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                ) : (
                  <i className="ri-delete-bin-line text-sm"></i>
                )}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-background-50 rounded-2xl border border-background-200/50 p-8 text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-background-100 flex items-center justify-center">
            <i className="ri-shield-check-line text-foreground-400 text-lg"></i>
          </div>
          <p className="text-foreground-500 text-sm">{t("blacklist.empty")}</p>
        </div>
      )}

      <p className="text-xs text-foreground-400">
        {t("blacklist.blockedCount", { count: blacklist.length })}
      </p>
    </div>
  );
}